import { useEffect } from "react";
import i18next from "i18next";
import { Settings } from "luxon";
import { toI18nLng } from "shared-ts-utils-i18n";

/**
 * Synchronizes the global i18next and Luxon singletons with a user-facing
 * BCP-47 locale (e.g. `nb-NO`).
 *
 * This hook owns both global-singleton side effects in one place:
 *
 * - It calls `i18next.changeLanguage` with the *mapped* i18n file basename
 *   (e.g. `no` for `nb-NO`), obtained from
 *   {@link toI18nLng}. The `supportedLngs` argument is read from i18next's
 *   own configuration at call time so that callers do not have to thread
 *   the array through manually. If i18next has not been initialized with a
 *   `supportedLngs` array, an empty array is passed and the mapper's
 *   second validation step is skipped (the unknown-locale throw still
 *   applies).
 *
 * - It sets Luxon's `Settings.defaultLocale` to the **user-facing** locale.
 *   Luxon (Intl under the hood) does not support `nn-NO`, so the existing
 *   `nn-NO → nb-NO` workaround is preserved verbatim.
 *
 * A single `console.info` is emitted per change naming both the
 * user-facing locale and the mapped i18n lng, so logs are correlatable.
 *
 * `I18NextProvider.language` remains init-only: runtime language changes
 * go exclusively through this hook.
 *
 * @param userFacingLanguage - A BCP-47 user-facing locale.
 *
 * @see toI18nLng
 * @see `docs/prd/2026/05/22/prd-i18n-language-mapping.md`
 */
export const useI18nLanguageSync = (userFacingLanguage: string): void => {
  useEffect(() => {
    const configuredSupportedLngs = i18next.options?.supportedLngs;
    const supportedLngs: ReadonlyArray<string> = Array.isArray(
      configuredSupportedLngs
    )
      ? (configuredSupportedLngs as ReadonlyArray<string>)
      : [];

    const mappedLng = toI18nLng(userFacingLanguage, supportedLngs);

    console.info(
      `Setting i18next language to: ${mappedLng} (user-facing: ${userFacingLanguage})`
    );
    i18next.changeLanguage(mappedLng);

    // Luxon (Intl) does not support `nn-NO`; fall back to `nb-NO` so date
    // and number formatting still works. Preserved verbatim from the
    // previous `LanguageUserSettingProvider` implementation; revisit later.
    const luxonLocale =
      userFacingLanguage === "nn-NO" ? "nb-NO" : userFacingLanguage;
    Settings.defaultLocale = luxonLocale;
  }, [userFacingLanguage]);
};
